import { configureStore, Middleware } from '@reduxjs/toolkit';
import {
  useDispatch as useAppDispatch,
  useSelector as useAppSelector,
  TypedUseSelectorHook,
} from 'react-redux';
import { persistStore, persistReducer } from 'redux-persist';
import { EventsApi } from './api/events.api';
import { AdminApi } from './api/admin.api';
import { rootPersistConfig, rootReducer } from './rootReducer';
import { MenusApi } from './api/menus.api';
import { UsersApi } from './api/users.api';
import { ContactsApi } from './api/contacts.api';
import { ReservationsApi } from './api/reservations.api';
import { SurveysApi } from './api/surveys.api';
import { EventCategoriesApi } from './api/eventCatygory.api';
import { emailTemplatesApi } from './api/emailTemplates.api';
import { TicketCategoriesApi } from './api/ticketCategories.api';
import { BusTripsApi } from './api/busTrips.api';
import { MailCampaignApi } from './api/mailcampaigns.api';
import { TicketsApi } from './api/tickets.api';
import { HiddenLinksApi } from './api/hiddenLinks.api';
import { FaqsApi } from './api/faqs.api';
import { ArtistLineupApi } from './api/artistLineup.api';
import { OrganizersApi } from './api/organisers.api';
import { PassCultureApi } from './api/PassCulture.api';
import { GoodiesApi } from './api/goodies.api';
import { AnalyticsApi } from './api/analytics.api';
import { PromocodeAnalyticsApi } from './api/promocodeAnalytics.api';
import { EnhancedOrganizationsApi } from './api/organizations.enhanced.api';
import { stripeApi } from './api/stripe.api';
import { shareBoostApi } from './api/shareboost.api';
import { engineersApi } from './api/engineers.api';
import { followersApi } from './api/followers.api';
import { organizationStripeApi } from './api/organizationStripe.api';
import { financialApi } from './api/financial.api';
import { CampaignApi } from './api/campaign.api';
import { affiliationsApi } from './api/affiliations.api';
import { CoOrganizersApi } from './api/coOrganizers.api';

// ----------------------------------------------------------------------

const middlewares: Middleware[] = [
  EventsApi.middleware,
  AdminApi.middleware,
  MenusApi.middleware,
  UsersApi.middleware,
  ContactsApi.middleware,
  SurveysApi.middleware,
  ReservationsApi.middleware,
  EventCategoriesApi.middleware,
  emailTemplatesApi.middleware,
  TicketCategoriesApi.middleware,
  TicketsApi.middleware,
  BusTripsApi.middleware,
  MailCampaignApi.middleware,
  HiddenLinksApi.middleware,
  FaqsApi.middleware,
  ArtistLineupApi.middleware,
  OrganizersApi.middleware,
  PassCultureApi.middleware,
  GoodiesApi.middleware,
  AnalyticsApi.middleware,
  PromocodeAnalyticsApi.middleware,
  EnhancedOrganizationsApi.middleware,
  stripeApi.middleware,
  shareBoostApi.middleware,
  engineersApi.middleware,
  followersApi.middleware,
  organizationStripeApi.middleware,
  financialApi.middleware,
  CampaignApi.middleware,
  affiliationsApi.middleware,
  CoOrganizersApi.middleware,
];

const store = configureStore({
  reducer: persistReducer(rootPersistConfig, rootReducer),
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
      immutableCheck: false,
    }).concat(middlewares),
  devTools: process.env.NODE_ENV !== 'production',
});

const persistor = persistStore(store);

export type RootState = ReturnType<typeof rootReducer>;

export type AppDispatch = typeof store.dispatch;

const { dispatch } = store;

const useDispatch = () => useAppDispatch<AppDispatch>();

const useSelector: TypedUseSelectorHook<RootState> = useAppSelector;

export { store, persistor, dispatch, useSelector, useDispatch };
